/**
 * Settings drawer.
 *
 * Slides in from the right on desktop and up from the bottom on mobile. Holds
 * the display preferences from the settings store plus, during a match, the
 * "copy replay JSON" button: a match is fully described by (seed, decks,
 * actions), so that one string is a complete bug report.
 */

import { useState, type ReactNode } from 'react';
import { useSettings, type CardSize } from '@/app/stores';
import { UiIcon } from '@/ui/icons/UiIcon';
import { Button } from './Button';
import { useToast } from './ToastProvider';
import styles from './SettingsDrawer.module.css';

export interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Rendered in a MATCH section. Absent outside a match. */
  matchExtras?: ReactNode;
}

const CARD_SIZES: { value: CardSize; label: string }[] = [
  { value: 'small', label: 'Small' },
  { value: 'normal', label: 'Normal' },
  { value: 'large', label: 'Large' },
];

export function SettingsDrawer({ open, onClose, matchExtras }: SettingsDrawerProps) {
  const settings = useSettings();

  if (!open) return null;

  return (
    <div className={styles.scrim} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <aside className={styles.drawer} role="dialog" aria-modal="true" aria-label="Settings">
        <header className={styles.header}>
          <h2 className={styles.title}>Settings</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close settings">
            <UiIcon name="close" size={16} />
          </button>
        </header>

        <div className={styles.body}>
          <Section label="Display">
            <Toggle
              label="Reduce motion"
              hint="Turns off card animations and plays bot turns without a delay."
              checked={settings.reduceMotion}
              onChange={() => settings.toggle('reduceMotion')}
            />
            <Toggle
              label="Show game log"
              hint="The log dock on the right of the battlefield."
              checked={settings.showLog}
              onChange={() => settings.toggle('showLog')}
            />
            <Choice
              label="Card size"
              value={settings.cardSize}
              options={CARD_SIZES}
              onChange={(value) => settings.set('cardSize', value)}
            />
          </Section>

          <Section label="Audio">
            <Toggle
              label="Sound"
              hint="No audio ships in this milestone. The switch is here so the setting persists."
              checked={settings.sound}
              onChange={() => settings.toggle('sound')}
            />
          </Section>

          {matchExtras ? <Section label="Match">{matchExtras}</Section> : null}
        </div>

        <footer className={styles.footer}>
          <Button variant="ghost" size="small" onClick={() => settings.reset()}>
            Reset to defaults
          </Button>
        </footer>
      </aside>
    </div>
  );
}

function Section({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className={styles.section}>
      <h3 className={styles.sectionTitle}>{label}</h3>
      <div className={styles.rows}>{children}</div>
    </section>
  );
}

function Toggle({
  label,
  hint,
  checked,
  onChange,
}: {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: () => void;
}) {
  return (
    <label className={styles.row}>
      <span className={styles.rowText}>
        <span className={styles.rowLabel}>{label}</span>
        {hint ? <span className={styles.rowHint}>{hint}</span> : null}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        className={styles.switch}
        data-on={checked}
        onClick={onChange}
      >
        <span className={styles.knob} />
      </button>
    </label>
  );
}

function Choice<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className={styles.row}>
      <span className={styles.rowText}>
        <span className={styles.rowLabel}>{label}</span>
      </span>
      <div className={styles.segmented} role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            className={styles.segment}
            data-on={option.value === value}
            aria-pressed={option.value === value}
            onClick={() => onChange(option.value)}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Copy the replay descriptor to the clipboard. Kept here so both the
 * battlefield settings section and any future debug panel share one
 * implementation, including the fallback for browsers without clipboard access.
 */
export function CopyReplayButton({ getReplay }: { getReplay: () => string }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  return (
    <Button
      variant="secondary"
      size="small"
      block
      icon={<UiIcon name="copy" size={14} />}
      onClick={async () => {
        const json = getReplay();
        try {
          await navigator.clipboard.writeText(json);
          setCopied(true);
          toast.push('Replay JSON copied.', 'success');
          setTimeout(() => setCopied(false), 1800);
        } catch {
          toast.push('Clipboard is not available here. The replay is in the console.', 'warn');
          console.log(json);
        }
      }}
    >
      {copied ? 'Copied' : 'Copy replay JSON'}
    </Button>
  );
}
